import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICONS } from '../../icons';
import MarkdownDisplay from './MarkdownDisplay';
import './markdownStyles.css';

/**
 * MarkdownEditor component - textarea with Edit/Preview toggle for admin panel
 * @param {Object} props
 * @param {string} props.value - Current markdown value
 * @param {function} props.onChange - Callback when value changes
 * @param {string} [props.placeholder] - Placeholder text for textarea
 * @param {number} [props.rows=4] - Number of rows for textarea
 * @param {string} [props.className] - Additional CSS classes
 */
export default function MarkdownEditor({
  value = '',
  onChange,
  placeholder = 'Enter description...',
  rows = 4,
  className = ''
}) {
  const [mode, setMode] = useState('edit'); // 'edit' or 'preview'
  const [showHelp, setShowHelp] = useState(false);

  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  return (
    <div className={`markdown-editor ${className}`}>
      <div className="markdown-editor-toolbar">
        <div className="mode-toggle">
          <button
            type="button"
            className={`mode-btn ${mode === 'edit' ? 'active' : ''}`}
            onClick={() => setMode('edit')}
          >
            <FontAwesomeIcon icon={ICONS.EDIT} />
            Edit
          </button>
          <button
            type="button"
            className={`mode-btn ${mode === 'preview' ? 'active' : ''}`}
            onClick={() => setMode('preview')}
          >
            <FontAwesomeIcon icon={ICONS.EYE} />
            Preview
          </button>
        </div>

        <button
          type="button"
          className="help-btn"
          onClick={() => setShowHelp(!showHelp)}
          title="Markdown syntax help"
        >
          <FontAwesomeIcon icon={ICONS.QUESTION} />
        </button>
      </div>

      {showHelp && (
        <div className="markdown-help">
          <div className="help-content">
            <div className="help-section">
              <h5>Text Formatting</h5>
              <code>**bold**</code> <span>bold</span><br />
              <code>*italic*</code> <span>italic</span><br />
              <code>~~strikethrough~~</code> <span>strikethrough</span>
            </div>
            <div className="help-section">
              <h5>Headers</h5>
              <code># Heading 1</code><br />
              <code>## Heading 2</code><br />
              <code>### Heading 3</code>
            </div>
            <div className="help-section">
              <h5>Lists</h5>
              <code>- bullet item</code><br />
              <code>1. numbered item</code><br />
              <code>- [x] task (done)</code>
            </div>
            <div className="help-section">
              <h5>Links & Media</h5>
              <code>[text](url)</code> <span>link</span><br />
              <code>![alt](image-url)</code> <span>image</span><br />
              <span className="help-note">Image/YouTube/Twitch/Twitter URLs auto-embed</span>
            </div>
          </div>
        </div>
      )}

      {mode === 'edit' ? (
        <textarea
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          rows={rows}
          className="markdown-textarea"
        />
      ) : (
        <div className="markdown-preview">
          {value ? (
            <MarkdownDisplay content={value} />
          ) : (
            <p className="preview-placeholder">Nothing to preview</p>
          )}
        </div>
      )}
    </div>
  );
}
